import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function PlacementUpdate() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    employee_id: "",
    full_name: "",
    designation: "",
    company_name: "",
    placement_year: "",
    location: "",
    package_lpa: "",
    isActive: 1,
  });

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  useEffect(() => {
    if (id) {
      fetchPlacement();
    }
  }, [id]);

  const fetchPlacement = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}placements/getplacedcandidate/${id}`
      );

      if (res.data && res.data.length > 0) {
        const data = res.data[0];
        setFormData(data);

        if (data.image) {
          setPreview(
            `${process.env.REACT_APP_API_URL}uploads/${data.image}`
          );
        }
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Failed to fetch placement data", {
        position: "top-right",
        autoClose: 2000,
        theme: "colored",
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);

    if (file) {
      setPreview(URL.createObjectURL(file));
    }
  };

  const validateForm = () => {
    if (!String(formData.employee_id).trim()) {
      toast.error("Employee ID is required", { position: "top-right", autoClose: 2000, theme: "colored" });
      return false;
    }
    if (!String(formData.full_name).trim()) {
      toast.error("Full Name is required", { position: "top-right", autoClose: 2000, theme: "colored" });
      return false;
    }
    if (!String(formData.designation).trim()) {
      toast.error("Designation is required", { position: "top-right", autoClose: 2000, theme: "colored" });
      return false;
    }
    if (!String(formData.company_name).trim()) {
      toast.error("Company Name is required", { position: "top-right", autoClose: 2000, theme: "colored" });
      return false;
    }
    if (!String(formData.placement_year).trim()) {
      toast.error("Placement Year is required", { position: "top-right", autoClose: 2000, theme: "colored" });
      return false;
    }
    return true;
  };

  const handleConfirmUpdate = async () => {
    setShowUpdateModal(false);

    if (!validateForm()) return;

    try {
      const data = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key === "package_lpa") {
          const val = (formData[key] === "" || formData[key] === null) ? "0" : formData[key];
          data.append(key, val);
        } else {
          data.append(key, formData[key]);
        }
      });

      if (image) {
        data.append("image", image);
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL}placements/updateplacedcandidate/${id}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("Updated Successfully", {
        position: "top-right",
        autoClose: 2000,
        theme: "colored",
      });
      setTimeout(() => {
        navigate("/placedCandidates");
      }, 2000);
    } catch (error) {
      toast.error("Update failed. Please try again.", {
        position: "top-right",
        autoClose: 2000,
        theme: "colored",
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowUpdateModal(true);
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container mt-4">
        <div className="form-card">
          <div className="form-card-header">
            Edit Placement
          </div>
          <div className="form-card-body">
            <Form onSubmit={handleSubmit}>

              {/* Row 1: Employee ID & Full Name */}
              <Row className="mb-3">
                <Form.Group as={Col} controlId="employee_id">
                  <Form.Label>Employee ID</Form.Label>
                  <Form.Control
                    type="number"
                    name="employee_id"
                    value={formData.employee_id || ""}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
                <Form.Group as={Col} controlId="full_name">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="full_name"
                    value={formData.full_name || ""}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Row>

              {/* Row 2: Designation & Company */}
              <Row className="mb-3">
                <Form.Group as={Col} controlId="designation">
                  <Form.Label>Designation</Form.Label>
                  <Form.Control
                    type="text"
                    name="designation"
                    value={formData.designation || ""}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
                <Form.Group as={Col} controlId="company_name">
                  <Form.Label>Company</Form.Label>
                  <Form.Control
                    type="text"
                    name="company_name"
                    value={formData.company_name || ""}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Row>

              {/* Row 3: Year & Location */}
              <Row className="mb-3">
                <Form.Group as={Col} controlId="placement_year">
                  <Form.Label>Year</Form.Label>
                  <Form.Control
                    type="number"
                    name="placement_year"
                    value={formData.placement_year || ""}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
                <Form.Group as={Col} controlId="location">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Row>

              {/* Row 4: Package (LPA) & Status */}
              <Row className="mb-3">
                <Form.Group as={Col} md={6} controlId="package_lpa">
                  <Form.Label>Package (LPA)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="package_lpa"
                    value={formData.package_lpa || ""}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
                <Form.Group as={Col} md={6} controlId="isActive">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="isActive"
                    value={formData.isActive}
                    onChange={handleChange}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Row>

              {/* Image Upload */}
              <Row className="mb-3">
                <Form.Group as={Col} controlId="imageUpload">
                  <Form.Label>Update Image</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleImageChange}
                  />
                </Form.Group>
                <Col>
                  {preview && (
                    <div className="image-preview-container">
                      <Form.Label>Preview:</Form.Label>
                      <div>
                        <img
                          src={preview}
                          alt="Preview"
                          loading="lazy"
                          className="image-preview"
                        />
                      </div>
                    </div>
                  )}
                </Col>
              </Row>

              {/* Form Actions */}
              <div className="form-actions">
                <button type="submit" className="btn-gradient-primary">
                  Update
                </button>
                <Link to="/placedCandidates" className="btn-back-link">
                  ← Back to List
                </Link>
              </div>
            </Form>
          </div>
        </div>
      </div>

      <ToastContainer />

      {/* Update Confirmation Modal */}
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to update this placement?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmUpdate}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}